import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Input,
  Button,
  Select,
  Typography,
  Form,
  message,
  Checkbox,
} from "antd";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../Context/AuthContext";
import "../css/login.css";

const { Title } = Typography;
const { Option } = Select;

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const apiUrl = process.env.REACT_APP_API_URL;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Load saved values from localStorage (if user previously chose "Remember me")
  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem("remember_email");
      const savedRole = localStorage.getItem("remember_role");
      const savedRemember = !!savedEmail; // if email exists, user had remembered
      form.setFieldsValue({
        userID: savedEmail || "",
        businessType: savedRole || undefined,
        remember: savedRemember,
      });
    } catch (e) {
      // ignore localStorage errors (e.g., private mode)
      console.warn("Could not read saved credentials:", e);
    }
  }, [form]);

  const handleSubmit = async (values) => {
    // Basic email format check
    if (!emailRegex.test(values.userID.toLowerCase())) {
      message.error("Enter a valid email");
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/api/auth/login`, {
        userId: values.userID.toLowerCase(),
        password: values.password,
        role: values.businessType,
      });

      if (response.data && response.data.success) {
        const token = response.data.token;
        console.log(response.data);

        // Persist token according to "remember me"
        try {
          if (values.remember) {
            // Persist token and credentials across browser sessions
            // localStorage.setItem("auth_token", token);

            localStorage.setItem(
              "auth_token",
              JSON.stringify({
                token,
                loginTime: Date.now()
              })
            );

            localStorage.setItem("remember_email", values.userID.toLowerCase());
            localStorage.setItem("remember_role", values.businessType || "");
            // make sure no stale session storage token remains
            sessionStorage.removeItem("auth_token");
          } else {
            // Keep token only for the session
            sessionStorage.setItem("auth_token", token);
            // remove any previously remembered credentials
            localStorage.removeItem("remember_email");
            localStorage.removeItem("remember_role");
            localStorage.removeItem("auth_token");
          }
        } catch (e) {
          console.warn("Storage error:", e);
        }

        // Call your auth context login (keeps existing behavior)
        login(token);

        message.success("Login Successful!");

        // Navigate based on role
        if (values.businessType === "AUDITOR") {
          navigate("/auditor-dashboard", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else {
        message.error("Incorrect email or password.");
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        message.error("You are not authorized for this role.");
      } else {
        console.error("Login error:", error);
        message.error("An error occurred. Please try again.");
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full ml-5 flex max-w-sm py-3">
        <img src="logo.png" alt="Company Logo" className="h-12 w-12" />
        <label className="text-400 ml-3 text-2xl font-semibold">
          Unavar Admin Dashboard
        </label>
      </div>

      <div className="w-full max-w-sm p-6 bg-white rounded-md shadow-md">
        <Title level={3} className="text-gray-600 font-semibold">
          Sign in to your account
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ remember: false }}
        >
          <Form.Item
            label="Select your Role"
            name="businessType"
            rules={[
              {
                required: true,
                message: "Please select your business type!",
              },
            ]}
          >
            <Select placeholder="Select Business Type">
              <Option className="boldGrey" value="SUPER_ADMIN">Super Admin</Option>
              <Option className="boldGrey" value="ACCOUNT_ADMIN">Account Admin</Option>
              <Option value="AUDIT_ADMIN">Audit Admin</Option>
              <Option value="AUDITOR">Auditor</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={<span className=" font-semibold">User Email</span>}
            name="userID"
            rules={[{ required: true, message: "Please input your user id!" }]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            label={<span className="flex justify-around items-center font-semibold">Password</span>}
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
          >
            <Input.Password placeholder="Password" />
          </Form.Item>

          <Form.Item>
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox>Remember me</Checkbox>
            </Form.Item>

            <NavLink
              to="/forgot-password"
              className="text-sm text-blue-600 ml-2 login-form-forgot"
            >
              Forgot password
            </NavLink>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" className="w-full bg-BlueGlobal">
              Continue
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default LoginPage;
